var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import Box from '../Box';
import Button from '../Button';
var EllipsisText = styled(Box)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  && {\n    display: ", ";\n    overflow: hidden;\n    text-overflow: ellipsis;\n    vertical-align: middle;\n\n    /* If maxLines is used (only works for block element */\n    -webkit-line-clamp: ", ";\n    line-clamp: ", ";\n    -webkit-box-orient: vertical;\n\n    /* If maxCharacters is used (typically when we're dealing with inline element) */\n    max-width: ", ";\n    word-break: break-word;\n  }\n"], ["\n  && {\n    display: ", ";\n    overflow: hidden;\n    text-overflow: ellipsis;\n    vertical-align: middle;\n\n    /* If maxLines is used (only works for block element */\n    -webkit-line-clamp: ", ";\n    line-clamp: ", ";\n    -webkit-box-orient: vertical;\n\n    /* If maxCharacters is used (typically when we're dealing with inline element) */\n    max-width: ",
    ";\n    word-break: break-word;\n  }\n"])), function (_a) {
    var $isInline = _a.$isInline;
    return ($isInline ? 'inline' : '-webkit-box');
}, function (_a) {
    var $maxLines = _a.$maxLines;
    return $maxLines;
}, function (_a) {
    var $maxLines = _a.$maxLines;
    return $maxLines;
}, function (_a) {
    var $maxCharacters = _a.$maxCharacters;
    return $maxCharacters === undefined ? undefined : $maxCharacters + "ch";
});
var StyledButton = styled(Button)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  && {\n    padding: 0;\n    min-width: max-content;\n  }\n\n  &&:hover,\n  &&:active {\n    color: inherit;\n  }\n\n  &&,\n  &&:hover,\n  &&:active {\n    text-decoration: ", ";\n  }\n"], ["\n  && {\n    padding: 0;\n    min-width: max-content;\n  }\n\n  &&:hover,\n  &&:active {\n    color: inherit;\n  }\n\n  &&,\n  &&:hover,\n  &&:active {\n    text-decoration: ",
    ";\n  }\n"])), function (props) {
    return props.$isTextUnderlined ? 'underline' : 'none';
});
/**
 * Kareo component that allows collapses/expands text based on a given max number of lines to show
 * If the max number of lines is surpassed, the text is truncated and concatenated with "..."
 * @storybook: [ReadMore](http://design.kareoprod.ent/design-system/?path=/story/components-data-display--read-more)
 */
var ReadMore = function (_a) {
    var text = _a.text, expandButtonText = _a.expandButtonText, collapseButtonText = _a.collapseButtonText, isButtonTextUnderlined = _a.isButtonTextUnderlined, buttonProps = _a.buttonProps, ellipsisTextProps = __rest(_a, ["text", "expandButtonText", "collapseButtonText", "isButtonTextUnderlined", "buttonProps"]);
    var $maxLines = ellipsisTextProps.$maxLines, $maxCharacters = ellipsisTextProps.$maxCharacters, $isInline = ellipsisTextProps.$isInline;
    var linesEllipsisRef = useRef(null);
    var _b = useState(true), isCollapsed = _b[0], setCollapsed = _b[1];
    var _c = useState(true), isClamped = _c[0], setClamped = _c[1];
    // Only used if maxCharacters provided
    var _d = useState(text), clampedText = _d[0], setClampedText = _d[1];
    var toggleCollapse = function () { return setCollapsed(!isCollapsed); };
    var handleTextState = function (isTextClamped) {
        setClamped(isTextClamped);
        setCollapsed(isTextClamped);
    };
    var handleMaxLines = function () {
        var linesEllipsisEl = linesEllipsisRef.current;
        if (linesEllipsisEl) {
            var linesEllipsisHeight = linesEllipsisEl === null || linesEllipsisEl === void 0 ? void 0 : linesEllipsisEl.offsetHeight;
            var fullTextHeight = linesEllipsisEl === null || linesEllipsisEl === void 0 ? void 0 : linesEllipsisEl.scrollHeight;
            var isTextClamped = linesEllipsisHeight < fullTextHeight;
            handleTextState(isTextClamped);
        }
        else {
            handleTextState(true);
        }
    };
    var handleMaxCharacters = function () {
        var isTextClamped = !$maxCharacters || $maxCharacters < text.length;
        handleTextState(isTextClamped);
        if (isTextClamped)
            setClampedText(text.substring(0, $maxCharacters));
    };
    // Handle updated maxLines
    useEffect(function () {
        if ($maxLines !== undefined) {
            handleMaxLines();
        }
    }, [$maxLines]);
    // Handle updated maxCharacters
    useEffect(function () {
        if ($maxCharacters !== undefined) {
            handleMaxCharacters();
        }
    }, [$maxCharacters]);
    var buttonText = isCollapsed ? expandButtonText : collapseButtonText;
    return (React.createElement(Box, { component: "span" },
        isCollapsed ? (React.createElement(EllipsisText, __assign({}, ellipsisTextProps, { "data-testid": "ellipsis-text", ref: linesEllipsisRef }), $maxCharacters !== undefined ? clampedText + "..." : text)) : (React.createElement("span", { "data-testid": "full-text" }, text)),
        isClamped ? (React.createElement(React.Fragment, null,
            ($isInline || !isCollapsed) && ' ',
            React.createElement(StyledButton, __assign({ variant: "text", color: "inherit", "$isTextUnderlined": isButtonTextUnderlined, "aria-label": "read-more-toggle-btn", disableRipple: true }, buttonProps, { onClick: toggleCollapse }), buttonText))) : null));
};
ReadMore.defaultProps = {
    expandButtonText: 'Read More',
    collapseButtonText: 'See Less',
    isButtonTextUnderlined: true,
};
ReadMore.displayName = 'KareoReadMore';
export default ReadMore;
var templateObject_1, templateObject_2;
